import {useMountedState} from "../../../hooks/useMountedState";
import ProjectDetails from "../../../models/ProjectDetails";
import {ProjectSource, Status} from "../../../models/enums";
import {useTranslation} from "react-i18next";
import {apiInstance} from "../../../api/api";
import LoggerService from "../../../services/LoggerService";
import NotificationService from "../../../services/NotificationService";
import {useEffect, useRef, useState} from "react";
import {useUpdatedRef} from "../../../hooks/useUpdatedRef";
import {useProjectDetailContext} from "../providers/ProjectDetailProvider";
import {paths, useNavigator} from "../../navigator";

const takeoffsTabsPaths = {
	[ProjectSource.JOB]: paths.takeoffs_jobs,
	[ProjectSource.QUOTE]: paths.takeoffs_quotes,
}

function useProjectDetails() {
	const [project, setProject] = useMountedState<ProjectDetails | undefined>(undefined);
	const [loadStatus, setLoadStatus] = useState(Status.IDLE);
	const [migrationStatus, setMigrationStatus] = useState(Status.IDLE);
	const [dismissedMigrationForProjectId, setDismissedMigrationForProjectId] = useState<string | undefined>(undefined);
	const {t} = useTranslation();
	const {navigateTo} = useNavigator()
	const shouldRefetchProject = useRef(false);

	const loadProjectDetails = async (projectId: string) => {
		setLoadStatus(Status.LOADING)
		try {
			const project = await apiInstance.projectsApi.fetchProject(projectId)
			setProject(project)
			setLoadStatus(Status.SUCCESS)
		}
		catch (err) {
			setLoadStatus(Status.ERROR)
			LoggerService.logError(err);
			NotificationService.errorNotification(t("common.error"), t("projects.details.fetchDetailsErrorDesc"))
		}
	};


	const loadProjectDetailsBySimproId = (simproId: number, projectSource: ProjectSource) => {
		setLoadStatus(Status.LOADING);
		(projectSource === ProjectSource.JOB ?
				apiInstance.projectsApi.fetchJobProject(simproId) :
				apiInstance.projectsApi.fetchQuoteProject(simproId)
		)
			.then(project => {
				setProject(project)
				setLoadStatus(Status.SUCCESS)
			})
			.catch(err => {
				setLoadStatus(Status.ERROR)
				navigateTo(takeoffsTabsPaths[projectSource])
				LoggerService.logError(err);
				NotificationService.errorNotification(t("common.error"), t("projects.details.fetchDetailsErrorDesc"))
			})
	};

	const migrateFromOtherCompany = async (projectId: string): Promise<boolean> => {
		setMigrationStatus(Status.LOADING)
		try {
			const migrated = await apiInstance.projectsApi.migrateFromOtherCompany(projectId)
			setProject(migrated)
			setMigrationStatus(Status.SUCCESS)
			NotificationService.successNotification(
				t("projects.details.migration.successTitle"),
				t("projects.details.migration.successDesc")
			)
			return true
		}
		catch (err) {
			setMigrationStatus(Status.ERROR)
			LoggerService.logError(err);
			NotificationService.errorNotification(t("common.error"), t("projects.details.migration.errorDesc"))
			return false
		}
	};

	const dismissMigrationPrompt = (projectId: string) => {
		setDismissedMigrationForProjectId(projectId)
	};

	return {
		project, loadStatus, shouldRefetchProject,
		loadProjectDetails,
		loadProjectDetailsBySimproId,
		migrateFromOtherCompany,
		migrationStatus,
		dismissedMigrationForProjectId,
		dismissMigrationPrompt,
	}
}

function useRefetchProjectTreeOnMount() {
	const {refreshProjectTreeAfterSimproUpdate, projectDetails} = useProjectDetailContext()
	const {shouldRefetchProject} = projectDetails
	const dataRef = useUpdatedRef({
		refreshProjectTreeAfterSimproUpdate,
		shouldRefetchProject
	})

	useEffect(() => {
		const {
			refreshProjectTreeAfterSimproUpdate,
			shouldRefetchProject
		} = dataRef.current

		if (shouldRefetchProject.current) {
			shouldRefetchProject.current = false;
			refreshProjectTreeAfterSimproUpdate();
		}

		return function unmount() {
			shouldRefetchProject.current = true
		}
	}, [dataRef])
}

export {takeoffsTabsPaths, useProjectDetails, useRefetchProjectTreeOnMount}